import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SectionHeader from "@/components/SectionHeader";
import { Megaphone, Calendar, Users, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

export default function MembersDashboard() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {}),
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {}),
      api.get("/users").then((r) => setMembers(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="members-dashboard">
      <SectionHeader title="Strefa Członków" />
      <p className="text-center mt-4" style={{ color: "var(--sr-cream-soft)" }}>
        Witaj, <span className="text-[var(--sr-fire)] font-semibold">{user?.name}</span>. Brotherhood. Loyalty. Honor.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mt-12">
        <Stat icon={<Megaphone />} label="Aktualności" value={announcements.length} />
        <Stat icon={<Calendar />} label="Nadchodzące wydarzenia" value={events.length} />
        <Stat icon={<Users />} label="Członków klubu" value={members.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-12">
        {/* Announcements */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl tracking-wider text-[var(--sr-cream)]">Aktualności</h3>
            <Megaphone className="text-[var(--sr-fire)]" size={20} />
          </div>
          <div className="space-y-3 scrollbar-thin overflow-y-auto pr-1" style={{ maxHeight: 420 }}>
            {announcements.map((a) => (
              <article key={a.id} className="sr-card p-5" data-testid={`announcement-${a.id}`}>
                <h4 className="font-display text-lg text-[var(--sr-fire-2)]">{a.title}</h4>
                <p className="text-sm mt-2" style={{ color: "var(--sr-cream-soft)" }}>{a.body}</p>
                <p className="text-xs mt-3" style={{ color: "var(--sr-muted)" }}>
                  {a.author} • {new Date(a.created_at).toLocaleDateString("pl-PL")}
                </p>
              </article>
            ))}
            {announcements.length === 0 && <p className="text-sm" style={{ color: "var(--sr-muted)" }}>Brak aktualności.</p>}
          </div>
        </div>

        {/* Events */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl tracking-wider text-[var(--sr-cream)]">Nadchodzące zloty</h3>
            <Calendar className="text-[var(--sr-fire)]" size={20} />
          </div>
          <div className="space-y-3 scrollbar-thin overflow-y-auto pr-1" style={{ maxHeight: 420 }}>
            {events.slice(0, 4).map((ev) => (
              <article key={ev.id} className="sr-card p-5" data-testid={`event-card-${ev.id}`}>
                <h4 className="font-display text-lg text-[var(--sr-fire-2)]">{ev.title}</h4>
                <p className="text-xs mt-1 font-nav uppercase tracking-widest" style={{ color: "var(--sr-cream-soft)" }}>
                  {new Date(ev.date).toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" })} • {ev.location}
                </p>
                <p className="text-sm mt-2" style={{ color: "var(--sr-cream-soft)" }}>{ev.description}</p>
              </article>
            ))}
            {events.length === 0 && <p className="text-sm" style={{ color: "var(--sr-muted)" }}>Brak wydarzeń.</p>}
            <Link to="/wydarzenia" className="block text-center mt-2"><span className="btn-ghost">Wszystkie wydarzenia</span></Link>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-10">
        <Link to="/forum" className="sr-card p-6 hover:border-[var(--sr-fire)] block" data-testid="dash-forum-link">
          <MessageSquare className="text-[var(--sr-fire)] mb-3" />
          <h3 className="font-display text-xl text-[var(--sr-cream)]">Forum klubowe</h3>
          <p className="text-sm mt-1" style={{ color: "var(--sr-muted)" }}>Rozmawiaj z braćmi.</p>
        </Link>
        <Link to="/czlonkowie" className="sr-card p-6 hover:border-[var(--sr-fire)] block" data-testid="dash-members-link">
          <Users className="text-[var(--sr-fire)] mb-3" />
          <h3 className="font-display text-xl text-[var(--sr-cream)]">Lista członków</h3>
          <p className="text-sm mt-1" style={{ color: "var(--sr-muted)" }}>Poznaj swój klub.</p>
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="sr-card p-6 flex items-center gap-4">
      <div className="text-[var(--sr-fire)]">{icon}</div>
      <div>
        <p className="font-nav text-xs uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>{label}</p>
        <p className="font-display text-3xl text-[var(--sr-cream)]">{value}</p>
      </div>
    </div>
  );
}
